import type { ApolloError } from '@apollo/client';
import { isValid, format } from 'date-fns';
import type { RecordStatus, RoleGroup } from '../../components/Teams/types';
import { errorMessage } from '../utils/messages';

/**
 * Converts a Hasura graphql unknown value to a string explicitly.
 * This is usually used for UUID and Timestamp types in Database and Hasura cannot translate them to the appropriate
 * Typescript types. Ideally in the future we should have a type translation layer to fix this issue, probably zeus
 * will fix this in the future.
 * @param value as unknown from graphql.
 * @returns string value.
 */
// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
const graphqlUnknownToString = (value: unknown) => value as string;

/**
 * Converts a Hasura graphql unknown value to a `RoleGroup | undefined` explicitly.
 * @param value as unknown from graphql.
 * @returns `RoleGroup | undefined` value.
 */
const graphqlUnknownToRoleGroup = (value: unknown) =>
  value === null
    ? undefined
    : // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      (value as RoleGroup | undefined);

/**
 * Converts a Hasura graphql unknown value to a `RoleGroup | undefined` explicitly.
 * @param value as unknown from graphql.
 * @returns `RoleGroup | undefined` value.
 */
const graphqlUnknownToRecordStatus = (value: unknown) =>
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  value as RecordStatus;

/**
 * Converts a Hasura graphql unknown value to a Date explicitly.
 * @param value as unknown from graphql.
 * @returns date value or undefined if it cannot be converted to a Date.
 */
const graphQlUnknownToDate = (value: unknown): Date => {
  if (typeof value === 'string') {
    const maybeDate = new Date(value);

    if (isValid(maybeDate)) {
      return maybeDate;
    }
  }

  // eslint-disable-next-line functional/no-throw-statements
  throw new Error('Invalid date received.');
};

/**
 * Handles the error from Apollo GraphQL Client.
 * @param e apollo client error.
 */
export const onApolloError =
  (notifyError: (message: string) => void) =>
  (e: ApolloError): void => {
    const permissionError = e.graphQLErrors.find(
      (error) => error.extensions.code === 'permission-error'
    );

    const genericConstraintViolationError = e.graphQLErrors.find(
      (error) => error.extensions.code === 'constraint-violation'
    );

    const teamRoleAssignmentUniquenessError = e.graphQLErrors.find(
      (error) =>
        error.extensions.code === 'constraint-violation' &&
        typeof error.extensions.path === 'string' &&
        error.extensions.path.includes('insert_event_team_role_assignment')
    );

    if (permissionError !== undefined) {
      return notifyError(errorMessage.permissionNotGranted);
    }

    if (teamRoleAssignmentUniquenessError !== undefined) {
      return notifyError(errorMessage.roleAlreadyExists);
    }

    if (genericConstraintViolationError !== undefined) {
      return notifyError(errorMessage.genericDataConstraintViolation);
    }

    return notifyError(e.message);
  };

export const genericScalars = {
  uuid: {
    encode: (e: unknown) => JSON.stringify(e),
    decode: (e: unknown) => graphqlUnknownToString(e),
  },
  role_group_t: {
    encode: (e: unknown) => JSON.stringify(e),
    decode: (e: unknown) => graphqlUnknownToRoleGroup(e),
  },
  date: {
    encode: (e: unknown) => JSON.stringify(e),
    decode: (e: unknown) => format(graphQlUnknownToDate(e), 'yyyy-MM-dd'),
  },
  timestamptz: {
    encode: (e: unknown) => JSON.stringify(e),
    // This will only produce the right type (string) for scalar type, will not convert the actual response to Date instance.
    decode: (e: unknown) => graphQlUnknownToDate(e).toISOString(),
  },
  record_status_t: {
    encode: (e: unknown) => JSON.stringify(e),
    decode: (e: unknown) => graphqlUnknownToRecordStatus(e),
  },
};
